import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { getCurrentOrgId } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { BasePageContext } from "../../../contexts/basepage-context";
import { Admin__FeatureManagementCategory, Admin__FeatureManagementOperation, useGetFeatureManagementLazyQuery, useUpdateFeatureManagementAssignmentMutation, } from '../../../services/feature-management/feature-management-service';
import { useGetOrgsListQuery } from '../../../services/org/org-service';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { FeatureManagementTranslationKeys } from '../constants';
import { FeatureManagement } from './feature-management';
import styles from './feature-management.module.scss';
import { FeatureAccessTab } from './feature-management.util';
import { OrgFeatureManagement } from './org-feature-management';
const ALL_ORGS_LIST_INPUT = {
    batchsize: 10000,
    offset: 0,
    sort: 'DEFAULT',
    sortascending: true,
};
const EARLY_ACCESS_GROUP_NAME = 'early_access_feature';
const TAB_TO_CATEGORY = {
    [FeatureAccessTab.GeneralAccess]: Admin__FeatureManagementCategory.GeneralAccess,
    [FeatureAccessTab.EarlyAccess]: Admin__FeatureManagementCategory.EarlyAccess,
};
const mapApiToFeatureData = (apiData, t) => {
    if (!apiData?.data)
        return [];
    const seen = new Set();
    return apiData.data.flatMap((group) => (group.features ?? []).flatMap((feature) => {
        if (seen.has(feature.featureId))
            return [];
        seen.add(feature.featureId);
        return [
            {
                id: feature.featureId,
                name: t(`${feature.featureId}.label`),
                description: (() => {
                    const key = `${feature.featureId}.infoTooltip`;
                    const translated = t(key);
                    return translated !== key ? translated : undefined;
                })(),
                orgs: (feature.assignedOrgs ?? []).map((org) => org.orgName),
                totalOrgsCount: (feature.assignedOrgs ?? []).length,
                category: group.groupName,
                docsUrl: group.groupName === EARLY_ACCESS_GROUP_NAME
                    ? group.docsUrl
                    : feature.docsUrl,
            },
        ];
    }));
};
export const FeatureManagementContainer = ({ currentOrgId: propCurrentOrgId, }) => {
    const currentOrgId = propCurrentOrgId ?? getCurrentOrgId();
    const { t } = useTranslation();
    const { metricsService } = BasePageContext();
    const { data: orgsData, loading: orgsLoading } = useGetOrgsListQuery(ALL_ORGS_LIST_INPUT);
    const allOrgs = useMemo(() => {
        const orgs = orgsData?.Data?.orgs;
        if (!orgs)
            return [];
        return orgs
            .filter(Boolean)
            .map(org => ({ id: org.orgId, name: org.orgName }));
    }, [orgsData]);
    const { data: featureManagementData, loading: featuresLoading, getFeatureManagement, } = useGetFeatureManagementLazyQuery();
    const { updateFeatureManagementAssignment, } = useUpdateFeatureManagementAssignmentMutation();
    const [allFeatures, setAllFeatures] = useState([]);
    const [selectedTab, setSelectedTab] = useState(FeatureAccessTab.GeneralAccess);
    const [searchValue, setSearchValue] = useState('');
    const savedOrgsRef = useRef({});
    useEffect(() => {
        if (currentOrgId === -1) {
            getFeatureManagement(undefined, TAB_TO_CATEGORY[selectedTab]);
        }
    }, [currentOrgId, selectedTab, getFeatureManagement]);
    useEffect(() => {
        setAllFeatures(mapApiToFeatureData(featureManagementData, t));
    }, [featureManagementData]);
    const features = useMemo(() => {
        if (!searchValue)
            return allFeatures;
        const lower = searchValue.toLowerCase();
        return allFeatures.filter(f => f.name.toLowerCase().includes(lower));
    }, [allFeatures, searchValue]);
    const tabs = useMemo(() => [
        {
            id: FeatureAccessTab.GeneralAccess,
            title: t(FeatureManagementTranslationKeys.GENERAL_ACCESS),
        },
        {
            id: FeatureAccessTab.EarlyAccess,
            title: t(FeatureManagementTranslationKeys.EARLY_ACCESS),
        },
    ], [t]);
    const handleSaveFeatureOrgs = useCallback((featureId, orgIds, onSuccess) => {
        const isClearing = orgIds.length === 0;
        if (isClearing) {
            delete savedOrgsRef.current[featureId];
        }
        const previousFeature = allFeatures.find(f => f.id === featureId);
        const previousOrgNames = previousFeature?.orgs ?? [];
        const previousOrgIds = allOrgs
            .filter(org => previousOrgNames.includes(org.name))
            .map(org => org.id);
        const previousOrgCount = previousFeature?.totalOrgsCount ?? 0;
        updateFeatureManagementAssignment({
            feature_identifier: featureId,
            org_identifiers: orgIds,
            operation: Admin__FeatureManagementOperation.Replace,
        }, () => {
            setAllFeatures(prevFeatures => prevFeatures.map(feature => feature.id === featureId
                ? {
                    ...feature,
                    orgs: allOrgs
                        .filter(org => orgIds.includes(org.id))
                        .map(org => org.name),
                    totalOrgsCount: orgIds.length,
                }
                : feature));
            const featureName = previousFeature?.name ?? featureId;
            const orgIdToName = new Map(allOrgs.map(org => [org.id, org.name]));
            const previousOrgList = previousOrgIds.map(id => orgIdToName.get(id) ?? id);
            const newOrgList = orgIds.map(id => orgIdToName.get(id) ?? id);
            const orgsAdded = Math.max(0, orgIds.length - previousOrgCount);
            const orgsRemoved = Math.max(0, previousOrgCount - orgIds.length);
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_FEATURE_ALLOCATION), {
                featureId,
                featureName,
                previousOrgList,
                newOrgList,
                orgsAdded,
                orgsRemoved,
                featureCategory: selectedTab,
            });
            const previousValue = previousOrgCount > 0;
            const newValue = orgIds.length > 0;
            if (previousValue !== newValue) {
                metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_FEATURE_OVERRIDE), {
                    featureId,
                    featureName,
                    previousValue,
                    newValue,
                    featureCategory: selectedTab,
                });
            }
            onSuccess?.();
        });
    }, [
        allFeatures,
        allOrgs,
        metricsService,
        selectedTab,
        updateFeatureManagementAssignment,
    ]);
    const handleSearch = useCallback((value) => {
        setSearchValue(value);
    }, []);
    useEffect(() => {
        if (!searchValue)
            return undefined;
        const timer = setTimeout(() => {
            metricsService?.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_FEATURE_SEARCH), { searchTerm: searchValue, featureCategory: selectedTab });
        }, 500);
        return () => clearTimeout(timer);
    }, [searchValue, metricsService, selectedTab]);
    const handleDisableModalCancel = useCallback((featureId) => {
        const feature = allFeatures.find(f => f.id === featureId);
        metricsService?.collectEvent(new UserAction(userActionTypeEnum.FEATURE_DISABLE_MODAL_CANCEL), {
            featureId,
            featureName: feature?.name ?? featureId,
            featureCategory: selectedTab,
        });
    }, [allFeatures, metricsService, selectedTab]);
    const handleToggleFeature = useCallback((featureId, enabled) => {
        setAllFeatures(prevFeatures => prevFeatures.map(feature => {
            if (feature.id !== featureId)
                return feature;
            if (!enabled) {
                savedOrgsRef.current[featureId] = {
                    orgs: feature.orgs,
                    count: feature.totalOrgsCount,
                };
                return {
                    ...feature,
                    enabled: false,
                    orgs: [],
                    totalOrgsCount: 0,
                };
            }
            const saved = savedOrgsRef.current[featureId];
            return {
                ...feature,
                enabled: true,
                orgs: saved ? saved.orgs : feature.orgs,
                totalOrgsCount: saved
                    ? saved.count
                    : feature.totalOrgsCount,
            };
        }));
    }, []);
    const isOrgView = currentOrgId !== -1;
    return (React.createElement(Vertical, { className: styles.pageWrapper },
        React.createElement(AdminPageHeader, { title: t(FeatureManagementTranslationKeys.TITLE), description: isOrgView
                ? t(FeatureManagementTranslationKeys.DESCRIPTION_EARLY_ACCESS)
                : undefined, tabs: !isOrgView ? tabs : undefined, selectedTabId: selectedTab, onTabChange: tabId => setSelectedTab(tabId), titleTestId: "feature-management-title", tabTestIdPrefix: "feature-management-tab" }),
        currentOrgId === -1 && (React.createElement(FeatureManagement, { loading: featuresLoading || orgsLoading, features: features, allOrgs: allOrgs, selectedTab: selectedTab, onSaveFeatureOrgs: handleSaveFeatureOrgs, onSearch: handleSearch, onToggleFeature: handleToggleFeature, onDisableModalCancel: handleDisableModalCancel })),
        isOrgView && (React.createElement(OrgFeatureManagement, { onSearch: orgSearchTerm => {
                if (orgSearchTerm) {
                    metricsService?.collectEvent(new UserAction(userActionTypeEnum.ORG_FEATURE_SEARCH), {
                        searchTerm: orgSearchTerm,
                        featureCategory: FeatureAccessTab.EarlyAccess,
                    });
                }
            }, onDisableModalCancel: (featureId, featureName) => {
                metricsService?.collectEvent(new UserAction(userActionTypeEnum.FEATURE_DISABLE_MODAL_CANCEL), {
                    featureId,
                    featureName,
                    featureCategory: FeatureAccessTab.EarlyAccess,
                });
            } }))));
};
//# sourceMappingURL=feature-management.container.js.map